import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useEducation } from "../../../hooks/useEducation";
import ContentViewer from "./ContentViewer";
import "./ClassPage.css";
import "../CourseDetail.css";
import CourseCard from "../CourseCard";
import "../index.css";

const ClassPage = () => {
  const { classItemId } = useParams();

  const location = useLocation();
  const classProgress = location.state?.classProgress;

  const [eduClass, setEduClass] = useState(null);
  const [contents, setContents] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    myCourses,
    getClassById,
    updateClassProgress,
    fetchBookmarks,
    bookmarks,
    bookmarksLoading,
    handleBookmarkCreate,
    handleBookmarkDelete,
    fetchMyCourses,
  } = useEducation();

  useEffect(() => {
    fetchMyCourses();
  }, [fetchMyCourses]);

  // Fetch class details and content using the hook
  useEffect(() => {
    const fetchClassDetails = async () => {
      setLoading(true);
      try {
        // Use the hook method instead of direct fetch
        const classData = await getClassById(classItemId);

        if (!classData) {
          throw new Error("Failed to fetch class details");
        }

        setEduClass(classData);
        setContents(classData.contents || []);

        // Select the first content by default
        if (classData.contents && classData.contents.length > 0) {
          setSelectedContent(classData.contents[0]);
        }

        // Update class status to in_progress if it's not completed
        if (classProgress && classProgress.status !== "completed") {
          updateClassProgress(classProgress.id, "in_progress");
        }

        // Load bookmarks for this class
        fetchBookmarks(classItemId);
      } catch (err) {
        console.error("Error fetching class details:", err);
        setError("Failed to load class content");
      } finally {
        setLoading(false);
      }
    };

    fetchClassDetails();
  }, [
    classItemId,
    classProgress,
    getClassById,
    updateClassProgress,
    fetchBookmarks,
  ]);

  const handleContentSelect = (content) => {
    setSelectedContent(content);
  };

  if (loading) {
    return <div className="class-page-loading">Loading class content...</div>;
  }

  if (error) {
    return <div className="class-page-error">{error}</div>;
  }

  if (!eduClass) {
    return <div className="class-page-error">Class not found</div>;
  }

  return (
    <div
      className="class-page education-container"
      style={{ padding: "20px", paddingTop: "50px" }}
    >
      <div style={{ display: "flex" }}>
        <div className="class-page-non-content">
          <div className="class-header">
            <h1>{eduClass.name}</h1>
          </div>
          <div style={{ marginBottom: "30px" }}>
            {classProgress && (
              <div className="class-status">
                Status:{" "}
                {classProgress.status === "completed"
                  ? "Completed"
                  : classProgress.status === "in_progress"
                  ? "In Progress"
                  : "Not Started"}
              </div>
            )}
          </div>
          <CourseCard
            course={eduClass.course}
            myCourses={myCourses}
            basic={true}
          />

          <div className="class-layout" style={{ marginTop: "25px" }}>
            <div className="course-contents-sidebar">
              <h2>Modules</h2>
              <ul>
                {contents.map((content) => (
                  <li
                    key={content.id}
                    className={`module-item ${
                      selectedContent && selectedContent.id === content.id
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleContentSelect(content)}
                  >
                    <div className="module-item-content">
                      <h3>{content.title}</h3>
                      <div className="module-meta">
                        <span className="content-type">
                          {content.content_type_name || "Unknown type"}:
                        </span>
                        {content.duration && (
                          <span className="content-duration">
                            {content.duration} min
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="content-area">
          {selectedContent ? (
            <ContentViewer
              content={selectedContent}
              classId={classItemId}
              bookmarks={bookmarks}
              bookmarksLoading={bookmarksLoading}
              onBookmarkCreate={handleBookmarkCreate}
              onBookmarkDelete={handleBookmarkDelete}
              classProgress={classProgress.id}
            />
          ) : (
            <div className="no-content-selected">
              <p>Select a module to view its content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassPage;
